import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { useStore } from "@/lib/store";
import { motion, AnimatePresence } from "framer-motion";
import { t } from "@/lib/i18n";

export default function Header() {
  const { cart, setCartOpen } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const itemCount = cart?.item_count || 0;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[color:var(--marcini-border)]" : "bg-transparent"
      }`}
    >
      <div className="grid grid-cols-3 items-center px-5 md:px-12 h-16 md:h-20">
        {/* Left: nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8 text-eyebrow text-[color:var(--marcini-fg)]">
          <NavLink to="/shop" data-testid="nav-shop" className="link-underline">{t.nav.shop}</NavLink>
          <NavLink to="/collections/nye-produkter" data-testid="nav-new" className="link-underline">Nye</NavLink>
          <NavLink to="/collections/marcini-hoodies-unisex" data-testid="nav-hoodies" className="link-underline">Hoodies</NavLink>
          <NavLink to="/collections/marcini-t-shirts-unisex" data-testid="nav-tshirts" className="link-underline">T-skjorter</NavLink>
          <NavLink to="/collections/marcini-capses-unisex" data-testid="nav-caps" className="link-underline">Caps</NavLink>
          <NavLink to="/lookbook" data-testid="nav-lookbook" className="link-underline">{t.nav.lookbook}</NavLink>
        </nav>
        <button
          data-testid="mobile-menu-open"
          onClick={() => setMobileOpen(true)}
          className="md:hidden justify-self-start p-2 -ml-2"
          aria-label="Åpne meny"
        >
          <Menu size={22} />
        </button>

        {/* Center: wordmark */}
        <Link
          to="/"
          data-testid="brand-logo"
          className="justify-self-center flex items-center"
          aria-label="Marcini"
        >
          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-marcini/artifacts/12zfnghs_ChatGPT%20Image%2030.%20aug.%202026%2C%2010_55_37.png"
            alt="Marcini"
            className="h-6 md:h-8 w-auto object-contain"
          />
        </Link>

        {/* Right: utilities */}
        <div className="justify-self-end flex items-center gap-3 md:gap-6 text-eyebrow">
          <Link to="/shop" data-testid="nav-search" aria-label={t.nav.search} className="hidden md:inline-flex"><Search size={18} /></Link>
          <Link to="/wishlist" data-testid="nav-wishlist" aria-label={t.nav.wishlist} className="p-1"><Heart size={18} /></Link>
          <button
            data-testid="cart-toggle"
            onClick={() => setCartOpen(true)}
            className="relative p-1"
            aria-label={t.nav.bag}
          >
            <ShoppingBag size={18} />
            {itemCount > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-2 text-[10px] font-medium bg-black text-white rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-[3px]"
              >{itemCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white z-[60] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center px-5 h-16 border-b border-[color:var(--marcini-border)]">
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-marcini/artifacts/12zfnghs_ChatGPT%20Image%2030.%20aug.%202026%2C%2010_55_37.png"
                alt="Marcini"
                className="h-6 w-auto object-contain"
              />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" aria-label={t.common.close} className="p-2 -mr-2">
                <X size={22} />
              </button>
            </div>
            <div className="p-8 flex flex-col gap-5 font-serif-editorial text-4xl">
              <Link to="/shop" onClick={() => setMobileOpen(false)}>{t.nav.shop}</Link>
              <Link to="/collections/nye-produkter" onClick={() => setMobileOpen(false)}>Nye produkter</Link>
              <Link to="/collections/marcini-hoodies-unisex" onClick={() => setMobileOpen(false)}>Hoodies</Link>
              <Link to="/collections/marcini-t-shirts-unisex" onClick={() => setMobileOpen(false)}>T-skjorter</Link>
              <Link to="/collections/marcini-capses-unisex" onClick={() => setMobileOpen(false)}>Caps</Link>
              <Link to="/lookbook" onClick={() => setMobileOpen(false)}>{t.nav.lookbook}</Link>
              <Link to="/wishlist" onClick={() => setMobileOpen(false)}>{t.nav.wishlist}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
